import { createFileRoute, useParams, useNavigate } from '@tanstack/react-router'
import { useState } from 'react'
import { ArrowLeft, Package, Undo2 } from 'lucide-react'
import { useOrder, useOrderHistory, useUpdateOrderStatus, useRefundOrderPayment } from '../../../../hooks/data/useOrders'
import { useAlertContext } from '@/providers/AlertProvider'
import type { Address, OrderItem, OrderHistoryEntry, OrderStatus } from '@/types/ecommerce'

function statusColor(s: string) {
  return (
    {
      Pending: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
      Processing: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
      Completed: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
      Cancelled: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
      Refunded: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400',
    }[s] ?? 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300'
  )
}

function money(amount: number) {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount)
}

/** Mirrors OrderDomain.Order.TransitionTo's matrix exactly (Backend-System/API-Engine/
 * Microservices/ServiceVault.OrderService/Domain/OrderDomain.cs) — Cancelled/Refunded are
 * terminal, Completed only reaches Refunded, and the UI must not offer anything the backend
 * would 409/400 as an "invalid-status-transition" domain rule violation. */
const ALLOWED_TRANSITIONS: Record<OrderStatus, OrderStatus[]> = {
  Pending: ['Processing', 'Cancelled'],
  Processing: ['Completed', 'Cancelled'],
  Completed: ['Refunded'],
  Cancelled: [],
  Refunded: [],
}

function formatAddress(a: Address | null) {
  if (!a) return null
  return (
    <div>
      <div className="font-medium text-gray-800 dark:text-gray-100">{a.name}</div>
      <div>{a.line1}</div>
      {a.line2 && <div>{a.line2}</div>}
      <div>{a.city}, {a.region} {a.postalCode}</div>
      <div>{a.country}</div>
      {a.phone && <div>{a.phone}</div>}
    </div>
  )
}

function OrderDetailPage() {
  const { id } = useParams({ strict: false }) as { id: string }
  const navigate = useNavigate()
  const order = useOrder(id)
  const updateStatus = useUpdateOrderStatus(id)
  const refundPayment = useRefundOrderPayment(id)
  const { success: showSuccess, error: showError, confirm } = useAlertContext()

  const [note, setNote] = useState('')
  const [refundOpen, setRefundOpen] = useState(false)
  const [refundAmount, setRefundAmount] = useState('')
  const [refundReason, setRefundReason] = useState('')

  const [cursorStack, setCursorStack] = useState<string[]>([])
  const currentCursor = cursorStack[cursorStack.length - 1]
  const history = useOrderHistory(id, currentCursor)

  if (order.isLoading) return <p className="p-6 text-gray-500">Loading order…</p>
  if (order.isError || !order.data) return <p className="p-6 text-red-600">Failed to load order — requires the manager role, or it doesn't exist.</p>

  const o = order.data
  const nextStatuses = ALLOWED_TRANSITIONS[o.status] ?? []

  const transition = (target: OrderStatus) => {
    confirm({
      title: `Move to ${target}`,
      message:
        target === 'Cancelled'
          ? `Cancel order ${o.orderNumber}? This cannot be undone.`
          : `Transition order ${o.orderNumber} from ${o.status} to ${target}?`,
      type: target === 'Cancelled' ? 'warning' : 'info',
      confirmText: `Move to ${target}`,
      cancelText: 'Back',
      showCancel: true,
      onConfirm: async () => {
        try {
          await updateStatus.mutateAsync({ status: target, note: note.trim() || undefined, rowVersion: o.rowVersion })
          showSuccess('Status updated', `Order is now ${target}.`)
          setNote('')
        } catch (err) {
          showError(
            'Update failed',
            (err as Error)?.message ||
              'Failed to update order status — it may have changed since this page loaded (refresh and retry).',
          )
        }
      },
    })
  }

  const submitRefund = async () => {
    const reason = refundReason.trim()
    if (!reason) return
    const amount = refundAmount.trim() ? Number(refundAmount) : undefined
    if (amount !== undefined && (Number.isNaN(amount) || amount <= 0)) {
      showError('Invalid amount', 'Refund amount must be a positive number, or left blank for a full refund.')
      return
    }
    try {
      await refundPayment.mutateAsync({ orderId: o.id, amount, reason })
      showSuccess('Refund submitted', amount ? `Refunded ${money(amount)}.` : 'Full refund submitted.')
      setRefundOpen(false)
      setRefundAmount('')
      setRefundReason('')
    } catch (err) {
      showError('Refund failed', (err as Error)?.message || 'Failed to submit the refund via BillingService.')
    }
  }

  return (
    <div className="p-2 md:p-4 space-y-4 max-w-3xl">
      <button
        onClick={() => navigate({ to: '/orders' })}
        className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
      >
        <ArrowLeft className="w-4 h-4" /> Back to orders
      </button>

      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
        <div className="flex items-center justify-between">
          <h1 className="text-xl font-bold text-gray-800 dark:text-white">Order {o.orderNumber}</h1>
          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${statusColor(o.status)}`}>{o.status}</span>
        </div>
        <dl className="mt-3 grid grid-cols-2 gap-x-4 gap-y-2 text-sm text-gray-600 dark:text-gray-400">
          <div><span className="text-gray-400">Order ID:</span> {o.id}</div>
          <div><span className="text-gray-400">Customer ID:</span> {o.customerId}</div>
          <div><span className="text-gray-400">Payment method:</span> {o.paymentMethod}</div>
          <div><span className="text-gray-400">Payment status:</span> {o.paymentStatus}</div>
          <div><span className="text-gray-400">Placed:</span> {new Date(o.createdAt).toLocaleString()}</div>
          {o.trackingNumber && <div><span className="text-gray-400">Tracking #:</span> {o.trackingNumber}</div>}
        </dl>
        {o.notes && (
          <p className="mt-3 text-sm text-gray-600 dark:text-gray-400"><span className="text-gray-400">Notes:</span> {o.notes}</p>
        )}

        {/* Status workflow — constrained to OrderDomain.TransitionTo's real matrix. */}
        {nextStatuses.length > 0 ? (
          <div className="mt-4 space-y-2">
            <input
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Note for this status change (optional)…"
              className="w-full rounded-lg border border-gray-200 dark:border-gray-700 bg-transparent px-3 py-2 text-sm"
            />
            <div className="flex flex-wrap gap-2">
              {nextStatuses.map((target) => (
                <button
                  key={target}
                  disabled={updateStatus.isPending}
                  onClick={() => transition(target)}
                  className={`px-3 py-2 rounded-lg text-sm text-white disabled:opacity-40 ${
                    target === 'Cancelled' ? 'bg-red-600' : target === 'Refunded' ? 'bg-purple-600' : 'bg-blue-600'
                  }`}
                >
                  Move to {target}
                </button>
              ))}
            </div>
          </div>
        ) : (
          <p className="mt-4 text-sm text-gray-500">This order is in a terminal status — no further status changes are possible.</p>
        )}

        {/* Refund — separate from the status workflow: hits BillingService's Stripe refund
            directly (billingApi.refundOrderPayment), independent of OrderService's own status
            machine. Only offered once the order has actually been paid/completed. */}
        {(o.status === 'Completed' || o.status === 'Refunded') && (
          <div className="mt-4 border-t border-gray-100 dark:border-gray-700 pt-4">
            {!refundOpen ? (
              <button
                onClick={() => setRefundOpen(true)}
                className="inline-flex items-center gap-1 px-3 py-2 rounded-lg border border-amber-300 text-amber-700 dark:border-amber-800 dark:text-amber-400 text-sm"
              >
                <Undo2 className="w-4 h-4" /> Refund payment
              </button>
            ) : (
              <div className="space-y-2">
                <div className="flex flex-wrap gap-2">
                  <input
                    value={refundAmount}
                    onChange={(e) => setRefundAmount(e.target.value)}
                    type="number"
                    min="0"
                    step="0.01"
                    placeholder={`Amount (blank = full ${money(o.total)})`}
                    className="rounded-lg border border-gray-200 dark:border-gray-700 bg-transparent px-3 py-2 text-sm w-64"
                  />
                  <input
                    value={refundReason}
                    onChange={(e) => setRefundReason(e.target.value)}
                    placeholder="Reason (required)…"
                    className="flex-1 min-w-[160px] rounded-lg border border-gray-200 dark:border-gray-700 bg-transparent px-3 py-2 text-sm"
                  />
                </div>
                <div className="flex gap-2">
                  <button
                    disabled={!refundReason.trim() || refundPayment.isPending}
                    onClick={submitRefund}
                    className="px-3 py-2 rounded-lg bg-amber-600 text-white text-sm disabled:opacity-40"
                  >
                    Submit refund
                  </button>
                  <button onClick={() => setRefundOpen(false)} className="px-3 py-2 rounded-lg text-sm text-gray-500">
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
        <h2 className="font-semibold text-gray-800 dark:text-gray-100 flex items-center gap-2 mb-3">
          <Package className="w-4 h-4" /> Items ({o.items.length})
        </h2>
        {o.items.length === 0 ? (
          <p className="text-sm text-gray-500">No line items.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-left text-gray-500 dark:text-gray-400">
                <tr>
                  <th className="py-1.5 font-medium">Item</th>
                  <th className="py-1.5 font-medium">SKU</th>
                  <th className="py-1.5 font-medium">Qty</th>
                  <th className="py-1.5 font-medium">Price</th>
                  <th className="py-1.5 font-medium">Total</th>
                </tr>
              </thead>
              <tbody>
                {o.items.map((item: OrderItem) => (
                  <tr key={item.id} className="border-t border-gray-100 dark:border-gray-700">
                    <td className="py-1.5">{item.name}</td>
                    <td className="py-1.5 text-gray-500 font-mono text-xs">{item.sku}</td>
                    <td className="py-1.5">{item.quantity}</td>
                    <td className="py-1.5">{money(item.price)}</td>
                    <td className="py-1.5">{money(item.total)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <dl className="mt-3 space-y-1 text-sm text-gray-600 dark:text-gray-400 ml-auto max-w-[200px]">
          <div className="flex justify-between"><span>Subtotal</span><span>{money(o.subtotal)}</span></div>
          <div className="flex justify-between"><span>Tax</span><span>{money(o.tax)}</span></div>
          <div className="flex justify-between"><span>Discount</span><span>-{money(o.discount)}</span></div>
          <div className="flex justify-between"><span>Shipping</span><span>{o.shippingCost === 0 ? 'Free' : money(o.shippingCost)}</span></div>
          <div className="flex justify-between font-semibold text-gray-800 dark:text-gray-100"><span>Total</span><span>{money(o.total)}</span></div>
        </dl>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
          <h2 className="font-semibold text-gray-800 dark:text-gray-100 mb-2">Shipping address</h2>
          <div className="text-sm text-gray-600 dark:text-gray-400">{formatAddress(o.shippingAddress) ?? '—'}</div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
          <h2 className="font-semibold text-gray-800 dark:text-gray-100 mb-2">Billing address</h2>
          <div className="text-sm text-gray-600 dark:text-gray-400">{formatAddress(o.billingAddress) ?? 'Same as shipping'}</div>
        </div>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
        <h2 className="font-semibold text-gray-800 dark:text-gray-100 mb-3">Timeline</h2>
        {history.isLoading ? (
          <p className="text-sm text-gray-500">Loading timeline…</p>
        ) : !history.data || history.data.items.length === 0 ? (
          <p className="text-sm text-gray-500">No history recorded.</p>
        ) : (
          <ul className="space-y-3">
            {history.data.items.map((h: OrderHistoryEntry) => (
              <li key={h.id} className="text-sm border-l-2 border-gray-200 dark:border-gray-700 pl-3">
                <div className="text-gray-800 dark:text-gray-100 font-medium">
                  {h.status}
                  {h.note && <span className="text-gray-500 font-normal"> — {h.note}</span>}
                </div>
                <div className="text-xs text-gray-500">
                  {new Date(h.createdAt).toLocaleString()}
                  {h.performedBy && ` · ${h.performedBy}`}
                </div>
              </li>
            ))}
          </ul>
        )}
        <div className="mt-3 flex items-center gap-3">
          {cursorStack.length > 0 && (
            <button onClick={() => setCursorStack((prev) => prev.slice(0, -1))} className="text-sm text-blue-600 hover:underline">
              ← Newer
            </button>
          )}
          {history.data?.hasMore && history.data.nextCursor && (
            <button
              onClick={() => setCursorStack((prev) => [...prev, history.data!.nextCursor!])}
              className="text-sm text-blue-600 hover:underline"
            >
              Older →
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

export const Route = createFileRoute('/(admin)/orders/$id/')({
  component: OrderDetailPage,
})
